import { postResetPassword } from 'api/auth'
import Spinner from 'components/Spinner'
import useLazeTimeout from 'hooks/useLazeTimeout'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import Ui from 'ui'
import ErrorLabel from 'ui/atoms/ErrorLabel'
import { objectHasPropWithValue } from 'utils/helpers'
import {
  error,
  initial,
  isError,
  isLoading,
  isSuccess,
  loading,
  success,
} from 'utils/remoteData'
import { newPasswordSchema, schemasValidate } from 'utils/schemas'

import AuthCard from './components/AuthCard'
import useVerifyResetPasswordCodeRequest from './hooks/useVerifyResetPasswordCodeRequest'

const SECONDS_TO_REDIRECT = 3

const ResetPasswordSchema = {
  password: newPasswordSchema,
}

function ResetPassword() {
  const [formValues, setFormValues] = React.useState({
    password: '',
  })

  const [formError, setFormError] = React.useState({
    password: false,
  })

  const [formErrorLabel, setFormErrorLabel] = React.useState({
    password: '',
  })
  const history = useHistory()
  const [hasFormError, setHasFormError] = React.useState(false)
  const [passwordRequest, setPasswordRequest] = React.useState(initial())
  const { verifyCodeRequest, code } = useVerifyResetPasswordCodeRequest()

  React.useEffect(() => {
    setHasFormError(objectHasPropWithValue(formError, true))
  }, [formError])

  const redirectAfterTimeout = useLazeTimeout(() => {
    history.push('/login')
  }, SECONDS_TO_REDIRECT * 1000)

  const onChange = (name) => (value) => {
    setFormValues((current) => ({ ...current, [name]: value }))
    formError[name] &&
      schemasValidate(
        [[name, value, ResetPasswordSchema[name]]],
        setFormError,
        setFormErrorLabel
      )
  }

  const onSubmit = async () => {
    const { password } = formValues
    const isValid = await schemasValidate(
      [['password', password, ResetPasswordSchema.password]],
      setFormError,
      setFormErrorLabel
    )
    if (!isValid) {
      return
    }
    try {
      setPasswordRequest(loading())
      const response = postResetPassword({ code, password })
      setPasswordRequest(success(response))
      toast.success('Success: Password reset. Redirecting to login...')
      redirectAfterTimeout()
    } catch (err) {
      setPasswordRequest(error(err.message))
    }
  }

  return (
    <>
      <Helmet>
        <title>Reset Password/Zentake</title>
      </Helmet>

      <AuthCard>
        {isSuccess(verifyCodeRequest) && (
          <>
            <h1 className="text-xl font-medium text-gray-700">
              Reset Password
            </h1>
            <div className="mt-6">
              <Ui.PasswordInput
                errorLabel={formErrorLabel.password}
                hasError={formError.password}
                label="New Password"
                name="password"
                onChange={onChange('password')}
                placeholder="Enter your new password"
                type="password"
                value={formValues.password}
              />
            </div>
            <div className="grid mt-6">
              <Ui.PrimaryButton
                disabled={hasFormError}
                loading={isLoading(passwordRequest)}
                onClick={onSubmit}
              >
                Reset
              </Ui.PrimaryButton>
            </div>

            {isError(passwordRequest) && (
              <div className="flex flex-1 justify-center mt-3">
                <ErrorLabel>{passwordRequest.error}</ErrorLabel>
              </div>
            )}
          </>
        )}
        {isLoading(verifyCodeRequest) && (
          <div className="flex flex-1 justify-center">
            <Spinner color="onLight" size="56" />
          </div>
        )}
        {isError(verifyCodeRequest) && (
          <div className="flex flex-1 justify-center">
            <ErrorLabel>{verifyCodeRequest.error}</ErrorLabel>
          </div>
        )}
      </AuthCard>
    </>
  )
}

export default ResetPassword
